import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  TextField,
  Autocomplete,
  Grid,
} from "@mui/material";
import {
  ViewButton,
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
  StyledDialogBoxTitle,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import { CustomPaper, autoSelectStyle } from "components/Styles/globalStyles";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import dayjs from "dayjs";

ProviderBusiness.propTypes = {
  providerWiseBusinesByDateRange: PropTypes.string.isRequired,
  isItByDsa: PropTypes.string.isRequired,
};

// ── DSA field resolvers (ported from PartialDisbursementsReport) ──────────────

const resolveDsaId = (dsaObj) => {
  if (!dsaObj) return undefined;
  const candidates = ["dsa_Id", "dsaId", "dsa_id", "DSA_Id", "DSAID", "id", "ID"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const idKey = Object.keys(dsaObj).find(
    (k) => k.toLowerCase().includes("id") && !isNaN(dsaObj[k])
  );
  return idKey ? dsaObj[idKey] : undefined;
};

const resolveDsaName = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaName", "dsa_Name", "dsa_name", "DSAName", "name", "Name"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  const nameKey = Object.keys(dsaObj).find((k) => k.toLowerCase().includes("name"));
  return nameKey ? dsaObj[nameKey] : "";
};

const resolveDsaStatus = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaStatus", "dsa_Status", "dsa_status", "DSAStatus", "status", "Status"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null) return dsaObj[key];
  }
  return "";
};

export default function ProviderBusiness({ providerWiseBusinesByDateRange, isItByDsa = "" }) {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const { monthIdData } = useBusinessMonthYear();
  const [monthId, setMonthId] = useState(0);
  const businessCodeRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const MInput = inputStyle();
  const [openDailog, setOpenDailog] = useState(false);
  const [financeName, setFinanceName] = useState("");
  const jwt_token = Cookie.get("jwt_token");
  const [providerBusinessData, setProviderBusinessData] = useState([]);
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  const [pinnedBottomData, setPinnedBottomData] = useState([]);
  const [footerData, setFooterData] = useState([]);
  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(currentMonthEnd);

  const [dsaList, setDsaList] = useState([]);
  const [selectedDsa, setSelectedDsa] = useState(null);

  const mainGridRef = useRef(null);
  const dialogGridRef = useRef(null);

  const mainCurrencyFields = [
    "totalDisbursedAmount",
    "revenue",
    "grossDsaPayoutAmount",
    "tdsAmount",
    "dsaRevenue",
  ];

  const dialogCurrencyFields = ["loanAmount", "revenue", "dsaPayoutValue", "companyRetention"];

  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };

  const computeAndPinMain = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setPinnedBottomData([]);
      return;
    }
    const totals = { finainacialInstitutionName: "Total" };
    mainCurrencyFields.forEach((field) => {
      totals[field] = rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
    });
    setPinnedBottomData([totals]);
  }, []);

  const computeAndPinDialog = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setFooterData([]);
      return;
    }
    const totals = { city: "Total" };
    dialogCurrencyFields.forEach((field) => {
      totals[field] = rows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
    });
    setFooterData([totals]);
  }, []);

  const onMainFilterChanged = useCallback(() => {
    if (!mainGridRef.current?.api) return;
    const visibleRows = [];
    mainGridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinMain(visibleRows);
  }, [computeAndPinMain]);

  const onDialogFilterChanged = useCallback(() => {
    if (!dialogGridRef.current?.api) return;
    const visibleRows = [];
    dialogGridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinDialog(visibleRows);
  }, [computeAndPinDialog]);

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClickOpenDailog = () => setOpenDailog(true);
  const handleCloseDailog = () => setOpenDailog(false);
  const handleCloseAlert = () => setOpen(false);

  const fetchDsaList = async () => {
    try {
      const res = await fetch(URLs.dsaMasterGetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch DSA list");
      const data = await res.json();
      const rawList = Array.isArray(data) ? data : data.data || [];
      // Only active DSAs
      setDsaList(rawList.filter((item) => resolveDsaStatus(item) === "S"));
    } catch {
      showAlert("Failed to fetch DSA list", "error");
    }
  };

  useEffect(() => {
    fetchDsaList();
  }, []);

  const fetchEntriesByMonthId = async () => {
    if (!startDate || !endDate) {
      showAlert("Please select both start and end dates", "error");
      return;
    }
    if (!selectedDsa) {
      showAlert("Please select a DSA", "error");
      return;
    }

    const dsaId = resolveDsaId(selectedDsa);
    if (dsaId === undefined || dsaId === null || dsaId === "") {
      showAlert("Selected DSA has no valid ID. Please contact support.", "error");
      return;
    }

    const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

    setLoading(true);
    try {
      // API: /all-dsa-provider-business?startDate=...&endDate=...&dsaId=...
      const url = `${providerWiseBusinesByDateRange}startDate=${formattedStartDate}&endDate=${formattedEndDate}&dsaId=${dsaId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setServicesData(data);
        computeAndPinMain(data);
      } else {
        throw new Error("Failed to fetch entries data");
      }
    } catch (error) {
      showAlert(`Failed to fetch data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = async (rowData) => {
    // field from main grid row — handle both possible spellings
    const finananceId = rowData.financeId ?? rowData.finananceId ?? rowData.finanaceId;
    setFinanceName(rowData.financialInstitutionName ?? rowData.finainacialInstitutionName ?? "");
    handleClickOpenDailog();
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      setLoading(true);
      try {
        const dsaId = resolveDsaId(selectedDsa);
        const url = `${URLs.khproviderbusinessbifurcatedreport}?financeId=${finananceId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&dsaId=${dsaId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setProviderBusinessData(data);
          computeAndPinDialog(data);
        } else {
          throw new Error("Failed to fetch entries data");
        }
      } catch (error) {
        showAlert(`Failed to fetch data: ${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (monthIdData && monthIdData.length > 0) {
      setMonthId(monthIdData[0].businessMonthID);
    }
  }, [monthIdData]);

  const handleDownloadMainExcel = () => {
    if (!servicesData.length) return;
    const exportColumns = [
      { headerName: "BANK NAME", field: "providerName" },
      { headerName: "DSA NAME", field: "dsaName" },
      { headerName: "Total Business", field: "totalDisbursedAmount" },
      { headerName: "Revenue", field: "revenue" },
      { headerName: "Total DSA Payout", field: "grossDsaPayoutAmount" },
      { headerName: "TDS Amount", field: "tdsAmount" },
      { headerName: "DSA Revenue", field: "dsaRevenue" },
    ];
    const headers = exportColumns.map((c) => c.headerName);
    const exportRows = servicesData.map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });
    const totalsRow = {};
    headers.forEach((h) => (totalsRow[h] = ""));
    totalsRow["BANK NAME"] = "Total";
    mainCurrencyFields.forEach((field, i) => {
      const total = servicesData.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
      if (exportColumns[i + 1])
        totalsRow[exportColumns[i + 1].headerName] = parseFloat(total.toFixed(2));
    });
    exportRows.push(totalsRow);
    const ws = utils.json_to_sheet(exportRows, { header: headers });
    const maxWidths = headers.map((h) => h.length);
    exportRows.forEach((row) => {
      headers.forEach((h, i) => {
        const val = row[h] != null ? row[h].toString() : "";
        maxWidths[i] = Math.max(maxWidths[i], val.length);
      });
    });
    ws["!cols"] = maxWidths.map((w) => ({ wch: w + 4 }));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "ProviderBusiness");
    writeFile(wb, "Provider_Business_Data.xlsx");
  };

  const handleDownloadDialogExcel = () => {
    if (!providerBusinessData.length) return;
    const exportColumns = [
      { headerName: "Location", field: "city" },
      { headerName: "Application Number", field: "applicationNumber" },
      { headerName: "Remarks", field: "remarks" },
      { headerName: "Loan Type", field: "loanType" },
      { headerName: "Status", field: "status" },
      { headerName: "Customer Name", field: "customerName" },
      { headerName: "Loan Amount", field: "loanAmount" },
      { headerName: "Source", field: "source" },
      { headerName: "Revenue (%)", field: "revenuePercentage" },
      { headerName: "Revenue", field: "revenue" },
      { headerName: "DSA Pay-out (%)", field: "dsaPayoutPercentage" },
      { headerName: "DSA Pay-out Value", field: "dsaPayoutValue" },
      { headerName: "Company Retention", field: "companyRetention" },
      { headerName: "Retention (%)", field: "retentionPercentage" },
      { headerName: "DSA Name", field: "dsaName" },
      { headerName: "Managing Director", field: "subManager" },
      { headerName: "CEO", field: "bmChName" },
      { headerName: "Employee Code", field: "employeeCode" },
      { headerName: "EMP NAME", field: "employeeName" },
      { headerName: "BANK NAME", field: "bankerName" },
    ];
    const headers = exportColumns.map((c) => c.headerName);
    const exportRows = providerBusinessData.map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });
    const totalsRow = {};
    headers.forEach((h) => (totalsRow[h] = ""));
    totalsRow["Location"] = "Total";
    dialogCurrencyFields.forEach((field) => {
      const col = exportColumns.find((c) => c.field === field);
      if (col) {
        totalsRow[col.headerName] = parseFloat(
          providerBusinessData
            .reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0)
            .toFixed(2)
        );
      }
    });
    exportRows.push(totalsRow);
    const ws = utils.json_to_sheet(exportRows, { header: headers });
    const maxWidths = headers.map((h) => h.length);
    exportRows.forEach((row) => {
      headers.forEach((h, i) => {
        const val = row[h] != null ? row[h].toString() : "";
        maxWidths[i] = Math.max(maxWidths[i], val.length);
      });
    });
    ws["!cols"] = maxWidths.map((w) => ({ wch: w + 4 }));
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "ProviderBusinessDetails");
    writeFile(wb, `${financeName}_Business_Details.xlsx`);
  };

  const ActionCellRendererProviderBusiness = (props) => {
    // eslint-disable-next-line react/prop-types
    if (props.node.rowPinned === "bottom") return null;
    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex).data;
    return (
      <ViewButton type="button" onClick={() => handleViewClick(rowData)}>
        <VisibilityIcon />
      </ViewButton>
    );
  };
  ActionCellRendererProviderBusiness.propTypes = {
    node: PropTypes.shape({ rowIndex: PropTypes.number.isRequired }).isRequired,
    api: PropTypes.shape({ getDisplayedRowAtIndex: PropTypes.func.isRequired }).isRequired,
  };

  const columns = [
    {
      headerName: "BANK NAME",
      field: "providerName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
      cellStyle: (params) =>
        params.node.rowPinned === "bottom" ? { fontWeight: "bold", color: "#ffffff" } : null,
    },
    {
      headerName: "DSA NAME",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
      cellStyle: (params) =>
        params.node.rowPinned === "bottom" ? { fontWeight: "bold", color: "#ffffff" } : null,
    },
    {
      headerName: "Total Business",
      field: "totalDisbursedAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value),
    },
    {
      headerName: "Revenue",
      field: "revenue",
      filter: true,
      sortable: true,
      minWidth: 230,
      flex: 1.5,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value),
    },
    {
      headerName: "Total DSA Payout",
      field: "grossDsaPayoutAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value),
    },
    {
      headerName: "TDS Amount",
      field: "tdsAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value),
    },
    {
      headerName: "DSA Revenue",
      field: "dsaRevenue",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value),
    },
    { headerName: "View", cellRenderer: ActionCellRendererProviderBusiness, width: 80 },
  ];

  const providerBusinessColumns = [
    {
      headerName: "Location",
      field: "city",
      filter: true,
      sortable: true,
      minWidth: 130,
      flex: 1,
      cellStyle: (params) =>
        params.node.rowPinned === "bottom" ? { fontWeight: "bold", color: "#ffffff" } : null,
    },
    {
      headerName: "Application Number",
      field: "applicationNumber",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Remarks",
      field: "remarks",
      filter: true,
      sortable: true,
      minWidth: 300,
      flex: 1,
    },
    {
      headerName: "Loan Type",
      field: "loanType",
      filter: true,
      sortable: true,
      minWidth: 120,
      flex: 1,
    },
    { headerName: "Status", field: "status", filter: true, sortable: true, minWidth: 150, flex: 1 },
    {
      headerName: "Customer Name",
      field: "customerName",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "Loan Amount",
      field: "loanAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value ?? 0),
    },
    { headerName: "Source", field: "source", filter: true, sortable: true, minWidth: 120, flex: 1 },
    {
      headerName: "Revenue (%)",
      field: "revenuePercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      headerName: "Revenue",
      field: "revenue",
      filter: true,
      sortable: true,
      minWidth: 230,
      flex: 1.5,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value || 0),
    },
    {
      headerName: "DSA Pay-out (%)",
      field: "dsaPayoutPercentage",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      headerName: "DSA Pay-out Value",
      field: "dsaPayoutValue",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value || 0),
    },
    {
      headerName: "Company Retention",
      field: "companyRetention",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: (params) => ({
        textAlign: "right",
        fontWeight: params.node.rowPinned === "bottom" ? "800" : "500",
        color: params.node.rowPinned === "bottom" ? "#ffffff" : "inherit",
      }),
      valueFormatter: (params) => fmtINR(params.value ?? 0),
    },
    {
      headerName: "Retention (%)",
      field: "retentionPercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      headerName: "DSA Name",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1,
    },
    {
      headerName: "Managing Director",
      field: "subManager",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    { headerName: "CEO", field: "bmChName", filter: true, sortable: true, minWidth: 200, flex: 1 },
    {
      headerName: "Employee Code",
      field: "employeeCode",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "EMP NAME",
      field: "employeeName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
    },
    {
      headerName: "BANK NAME",
      field: "bankerName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
  ];

  const triggerApiCall = () => {
    businessCodeRef.current = monthId;
    fetchEntriesByMonthId();
  };

  return (
    <>
      {/* Loading Dialog */}
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Provider Business Detail Dialog */}
      <Dialog
        open={openDailog}
        onClose={handleCloseDailog}
        fullScreen
        sx={{
          ".MuiDialog-paper": {
            width: "90%",
            height: "90%",
            maxWidth: "none",
            minWidth: "500px",
            margin: "auto",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle>
          <StyledDialogBoxTitle>{financeName} Business</StyledDialogBoxTitle>
          <Box sx={{ position: "absolute", right: 8, top: 8, display: "flex", gap: 1 }}>
            <StyledButton onClick={handleDownloadDialogExcel}>
              <DownloadForOfflineIcon fontSize="medium" />
            </StyledButton>
            <StyledButton onClick={handleCloseDailog}>Close</StyledButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              ref={dialogGridRef}
              rowData={providerBusinessData}
              columnDefs={providerBusinessColumns}
              pagination={true}
              paginationPageSize={10}
              pinnedBottomRowData={footerData}
              onFilterChanged={onDialogFilterChanged}
              getRowStyle={getRowStyle}
              animateRows={true}
            />
          </StyledAgGridContainer>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {/* ── Main Table ── */}
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          {/* Filter bar — Start Date / End Date / Select DSA / Get button */}
          <Grid container spacing={2} alignItems="center" wrap="wrap" sx={{ mb: 1 }}>
            {/* Start Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={1.5}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                sx={{ ...MInput.b, maxWidth: "160px" }}
                InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
              />
            </Grid>

            {/* Select DSA */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete
                options={dsaList}
                getOptionLabel={(option) => resolveDsaName(option)}
                value={selectedDsa}
                onChange={(_, v) => setSelectedDsa(v || null)}
                filterOptions={(options, { inputValue }) => {
                  const input = inputValue.toLowerCase();
                  return options
                    .sort((a, b) => {
                      const aName = resolveDsaName(a).toLowerCase();
                      const bName = resolveDsaName(b).toLowerCase();
                      const aS = aName.startsWith(input);
                      const bS = bName.startsWith(input);
                      return aS === bS ? 0 : aS ? -1 : 1;
                    })
                    .filter((o) => resolveDsaName(o).toLowerCase().includes(input));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select DSA"
                    variant="outlined"
                    fullWidth
                    required
                    sx={{ height: 43, ...autoSelectStyle, ...MInput.b }}
                    InputLabelProps={{ sx: { color: MInput.a } }}
                  />
                )}
                PaperComponent={CustomPaper}
              />
            </Grid>

            {/* Get button */}
            <Grid item xs={12} sm={6} md={1.5}>
              <StyledButton onClick={triggerApiCall} fullWidth>
                Get
              </StyledButton>
            </Grid>

            {/* Excel download */}
            <Grid item xs="auto">
              <StyledButton onClick={handleDownloadMainExcel}>
                <DownloadForOfflineIcon fontSize="medium" />
              </StyledButton>
            </Grid>
          </Grid>
        </Box>

        {/* AG Grid */}
        <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
          <AgGridReact
            ref={mainGridRef}
            rowData={servicesData}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={pinnedBottomData}
            onFilterChanged={onMainFilterChanged}
            getRowStyle={getRowStyle}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </>
  );
}
